// @flow @format
import React, {Component, type ComponentType} from 'react';
import {connect} from 'react-redux';
import {fetchUsers} from '../../../reducers/entities/users';
import {fetchMachines} from '../../../reducers/entities/machines';
import {fetchRepositories} from '../../../reducers/entities/repositories';
import {fetchFileNode} from '../../../reducers/entities/files';
import {
	selectUser,
	selectMachine,
	selectFileNode,
	selectRepository,
} from '../../../reducers/selection';

import type {State} from '../../../reducers';
import type {State as Selection} from '../../../reducers/selection';
import type {State as Session} from '../../../reducers/session';
import type {Machine} from 'lib/cloner-api';
import type {Node} from 'react';

type Props = {
	session: Session,
	selection: Selection,
	machines: Machine[],

	children: Node,

	usersLoaded: boolean,
	machinesLoaded: boolean,
	repositoriesLoaded: boolean,
	fileNodeLoaded: boolean,

	location: {query: {[key: string]: ?string}},
	params: {[key: string]: ?string},

	fetchUsers: typeof fetchUsers,
	fetchMachines: typeof fetchMachines,
	fetchRepositories: typeof fetchRepositories,
	fetchFileNode: typeof fetchFileNode,

	selectUser: typeof selectUser,
	selectMachine: typeof selectMachine,
	selectRepository: typeof selectRepository,
	selectFileNode: typeof selectFileNode,
};

/**
 * RouteSelection watch for any changes on the route to fetch and select users, machines,
 * repositories and filenodes.
 *
 * Is the component implementation of the withRouteSelection HOC.
 */
class RouteSelection extends Component<Props> {
	componentWillMount() {
		const {location, selection, session} = this.props;

		// select user specified by URL first, if no user is specified we stay with the
		// currently selected and as a fallback we choose the user of the session.
		this.props.selectUser(
			location.query.userId || selection.userId || session.user
		);

		this.props.fetchUsers(session.group);

		if (selection.userId) {
			this.props.fetchMachines(selection.userId);
		}

		// skip the rest of steps if users or machines are not loaded yet.
		if (!this.props.usersLoaded || !this.props.machinesLoaded) {
			return;
		}

		// select machine specified by URL first, if no machine is specified we stay with the
		// currently selected and as a fallback we choose randomly an user machine.
		const machine = this.props.machines[0];
		this.props.selectMachine(
			location.query.machineId ||
				selection.machineId ||
				(machine && machine.mid)
		);

		if (selection.machineId && selection.userId) {
			this.props.fetchRepositories(selection.machineId, selection.userId);
		}

		// check if we need to fetch a filenode
		const {repositoryId, fileId} = this.props.params;

		if (repositoryId && fileId) {
			this.props.selectRepository(repositoryId);
			this.props.selectRepository(fileId);
			this.props.fetchFileNode(fileId, repositoryId);
		}
	}

	componentWillReceiveProps(nextProps: Props) {
		if (!nextProps.selection.userId) {
			this.props.selectUser(
				nextProps.location.query.userId ||
					nextProps.selection.userId ||
					nextProps.session.user
			);
			return;
		}

		// check if user selection change
		if (
			nextProps.location.query.userId &&
			nextProps.location.query.userId !== nextProps.selection.userId
		) {
			this.props.selectUser(nextProps.location.query.userId);
		}

		if (
			nextProps.selection.userId &&
			nextProps.selection.userId !== this.props.selection.userId
		) {
			this.props.fetchMachines(nextProps.selection.userId);
			this.props.selectMachine(null);
			this.props.selectRepository(null);
			this.props.selectFileNode(null);
		}

		// skip the rest of steps until machines are loaded
		if (!nextProps.machinesLoaded) {
			return;
		}

		if (!nextProps.selection.userId) {
			this.props.selectUser(
				nextProps.location.query.userId ||
					nextProps.selection.userId ||
					nextProps.session.user
			);
			return;
		}

		if (!nextProps.selection.machineId && nextProps.machines.length > 0) {
			const machine = nextProps.machines[0];
			this.props.selectMachine(
				nextProps.location.query.machineId || (machine && machine.mid)
			);
			return;
		}

		// check if machine selection change
		if (
			nextProps.location.query.machineId &&
			nextProps.location.query.machineId !== nextProps.selection.machineId
		) {
			this.props.selectMachine(
				nextProps.location.query.machineId ||
					nextProps.selection.machineId
			);
			return;
		}

		if (
			nextProps.selection.userId &&
			nextProps.selection.machineId &&
			nextProps.selection.machineId !== this.props.selection.machineId
		) {
			this.props.fetchRepositories(
				nextProps.selection.machineId,
				nextProps.selection.userId
			);
			this.props.selectRepository(null);
			this.props.selectFileNode(null);
		}

		// check for filenode changes
		const {repositoryId, fileId} = nextProps.params;

		if (repositoryId && repositoryId !== nextProps.selection.repositoryId) {
			nextProps.selectRepository(repositoryId);
		}

		if (fileId && fileId !== nextProps.selection.fileNodeId) {
			nextProps.selectFileNode(fileId);
		}

		if (
			repositoryId &&
			fileId &&
			(fileId !== nextProps.selection.fileNodeId ||
				repositoryId !== nextProps.selection.repositoryId)
		) {
			nextProps.fetchFileNode(fileId, repositoryId);
		}
	}

	shouldComponentUpdate() {
		return false;
	}

	render() {
		return this.props.children;
	}
}

const mapStateToProps = ({selection, session, entities}: State) => ({
	session,
	selection,
	machines: entities.machines.byUser[selection.userId],
	usersLoaded: !!entities.users.byGroup[session.group],
	machinesLoaded: !!entities.machines.byUser[selection.userId],
	repositoriesLoaded: !!entities.repositories.byMachine[selection.machineId],
	fileNodeLoaded: !!entities.files.entities[selection.fileNodeId],
});

const mapDispatchToProps = {
	fetchUsers,
	fetchMachines,
	fetchRepositories,
	fetchFileNode,
	selectUser,
	selectMachine,
	selectRepository,
	selectFileNode,
};

const RouteSelectionContainer = connect(mapStateToProps, mapDispatchToProps)(
	RouteSelection
);

type RouteProps = {
	location: {query: {[key: string]: ?string}},
	params: {[key: string]: ?string},
};

/**
 * HOC that watchs for any changes on the route to fetch and select users, machines,
 * repositories and filenodes.
 *
 * e.g. const KomponentWithSelection = withRouteSelection(Komponent);
 */
export default function withRouteSelection<Props: RouteProps>(
	WrappedComponent: ComponentType<Props>
) {
	return class extends Component<Props> {
		render() {
			return (
				<RouteSelectionContainer
					params={this.props.params}
					location={this.props.location}
				>
					<WrappedComponent {...this.props} />
				</RouteSelectionContainer>
			);
		}
	};
}



// WEBPACK FOOTER //
// ./src/routes/Explorer/hoc/withRouteSelection.js